import Vue from 'vue'
import Vuex from 'vuex'
import user, { UserState } from './modules/user-store-module'
import feature, { FeatureState } from './modules/feature-store-module'
import workspace, { WorkspaceState, QueryWorkspace } from './modules/workspace-store-module'
import testCreation, { TestCreationState } from './modules/test-creation-store-module'
import notification from './modules/notification-store-module'
import survey, { SurveyState } from './modules/survey-store-module'
import surveyConfiguration, { SurveyConfigurationState } from './modules/survey-configuration-store-module'
import livechartRefs, { LivechartRefState } from './modules/livechart-reference-store-module'

import { createDirectStore } from 'direct-vuex'
import { Session, WorkspaceWithTests } from '@shared/types/graphql'
import api from '@/api'

export interface ErrorMessage {
  statusCode?: number
  header?: string
  body?: string
}
export interface RootState {
  workspace: WorkspaceState
  user: UserState // We the submodules in the interface in order to access store submodules outside of components (Vue router), otherwise Typescript will complain,
  testCreation: TestCreationState
  feature: FeatureState
  survey: SurveyState
  surveyConfiguration: SurveyConfigurationState
  livechartRefs: LivechartRefState
  accountId?: string
  errorMessage?: ErrorMessage
}

export const NotFoundErrorMessage: ErrorMessage = {
  statusCode: 404,
  header: "Sorry, we couldn't find this page.",
  body: 'The link is broken or the page has been moved'
}

Vue.use(Vuex)

const state: RootState = {
  errorMessage: undefined
} as RootState // You have to cast it here else it complains about not having a user property initialization... however vuex composes the submodules itself so you can't actually provide a def

const {
  store,
  rootActionContext,
  moduleActionContext,
  moduleGetterContext
} = createDirectStore({
  state,
  modules: {
    user,
    workspace,
    notification,
    testCreation,
    survey,
    surveyConfiguration,
    livechartRefs,
    feature
  },
  mutations: {
    setAccountId (state, accountId: string) {
      state.accountId = accountId
    },
    setErrorMessage (state, errorMessage: ErrorMessage) {
      state.errorMessage = errorMessage
    }
  },
  actions: {
    /**
     * Resets the states of all child stores. This action is used to reset
     * application state on logout.
     */
    reset (context): void {
      const { dispatch } = rootActionContext(context)
      dispatch.testCreation.reset()
      dispatch.workspace.reset()
      dispatch.survey.reset()
      dispatch.notification.reset()
      dispatch.surveyConfiguration.reset()
    },
    /**
     * This action is called on login and application boot to populate child stores
     * with information about the authenticated user.
     */
    setSession (context, session: Session) {
      const { dispatch } = rootActionContext(context)
      dispatch.user.setSession(session)
    },
    /**
     * This action is called when entering the scope of a new workspace to
     * populate child store with information about the current workspace
     * and its parent account.
     */
    async setWorkspace (context, workspaceId: string): Promise<{ workspace: QueryWorkspace } | undefined> {
      const { commit, dispatch } = rootActionContext(context)
      var [workspace, sharedReports] = await Promise.all([api.getWorkspaceWithTests(workspaceId), api.getSharedReports(workspaceId)])
      await dispatch.reset()
      if (workspace && sharedReports) {
        commit.setAccountId(workspace.accountId)
        dispatch.workspace.setWorkspace(workspace)
        commit.workspace.setSharedReports(sharedReports)
        return { workspace }
      } else {
        dispatch.setErrorMessage(NotFoundErrorMessage)
      }
    },
    async updateWorkspace (context, workspaceId: string): Promise<any | WorkspaceWithTests> {
      if (!workspaceId) return
      const { dispatch, rootState } = rootActionContext(context)
      if (workspaceId !== rootState.workspace.workspace.id) {
        return dispatch.setWorkspace(workspaceId)
      }
    },
    setErrorMessage (context, errorMessage: ErrorMessage) {
      const { commit } = rootActionContext(context)
      commit.setErrorMessage(errorMessage)
    }
  }
})

// Export the direct-store instead of the classic Vuex store.
export default store

// The following exports will be used to enable types in the
// implementation of actions.
export { rootActionContext, moduleActionContext, moduleGetterContext }

// The following lines enable types in the injected store '$store'.
export type AppStore = typeof store
declare module 'vuex' {
  interface Store<S> {
    direct: AppStore
  }
}
